import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Text from 'core-system/Text'
import { AppState } from 'redux/config/store'
import { Segment } from 'redux/employer/employerTypes'
import { SegmentListType } from 'features/Programs/Shared/types'
import { AutoEnrollRule } from '../autoEnrollTypes'
import RuleCard from './RuleCard'

const ListRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`

const ListHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
`

const EmptyRoot = styled.div`
  margin-top: 1.25rem;
  padding: 2.5rem 1.5rem;
  text-align: center;
  border: 1px dashed ${(p) => p.theme.palette.grey.grey400};
  border-radius: 0.75rem;
  background: ${(p) => p.theme.palette.grey.grey100};
`

interface AutoEnrollRuleListProps {
  rules: AutoEnrollRule[]
  onDeleteRule: (id: string) => void
  onUpdateRule: (
    id: string,
    changes: Partial<Omit<AutoEnrollRule, 'id'>>
  ) => void
}

const AutoEnrollRuleList: React.FC<AutoEnrollRuleListProps> = ({
  rules,
  onDeleteRule,
  onUpdateRule,
}) => {
  const segmentsMap = useSelector(
    (state: AppState) => state.employer.segmentsMap
  )
  const tagsMap = useSelector((state: AppState) => state.tags.tagsMap)

  const pickerItems: SegmentListType[] = useMemo(() => {
    if (!segmentsMap) return []
    return (
      Object.values(segmentsMap).filter(
        (s: Segment) => s.type === 'CUSTOM' && !s.archived
      ) as Segment[]
    ).map((g) => ({ id: g.id, text: g.name, type: 'CUSTOM' }))
  }, [segmentsMap])

  const tagPickerItems: SegmentListType[] = useMemo(() => {
    if (!tagsMap) return []
    return Object.values(tagsMap).map((tag) => ({
      id: tag.id,
      text: tag.name,
      type: 'CUSTOM' as const,
    }))
  }, [tagsMap])

  const hasGroups = pickerItems.length > 0
  const hasTags = tagPickerItems.length > 0

  const targetSummary = (rule: AutoEnrollRule) => {
    const groupNames = (rule.segments ?? [])
      .map((id) => segmentsMap?.[id]?.name ?? id)
      .filter(Boolean)
    const tagNames = (rule.tags ?? [])
      .map((id) => tagsMap?.[id]?.name ?? id)
      .filter(Boolean)
    const parts: string[] = []
    if (groupNames.length) {
      parts.push(groupNames.join(', '))
    }
    if (tagNames.length) {
      parts.push(`tags: ${tagNames.join(', ')}`)
    }
    return parts.join(' | ') || '—'
  }

  if (!rules.length) {
    return (
      <EmptyRoot>
        <Text variant='action3' marginBottom='0.5rem' color='grey800'>
          No rules yet
        </Text>
        <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
          <Text variant='body2' color='grey600'>
            Rules run after sync, onboarding, and worksite changes. Build your
            first rule above to start routing employees into the right groups
            automatically.
          </Text>
        </div>
      </EmptyRoot>
    )
  }

  return (
    <section aria-label='Configured auto-enroll rules'>
      <ListHeader>
        <Text variant='action3' color='grey800'>
          Configured rules
        </Text>
        <Text variant='caption' color='grey600'>
          {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
        </Text>
      </ListHeader>
      <ListRoot>
        {rules.map((rule) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            targetSummary={targetSummary(rule)}
            groupPickerItems={hasGroups ? pickerItems : []}
            tagPickerItems={hasTags ? tagPickerItems : []}
            showEnrollmentControls={hasGroups || hasTags}
            onDelete={onDeleteRule}
            onSave={onUpdateRule}
          />
        ))}
      </ListRoot>
    </section>
  )
}

export default React.memo(AutoEnrollRuleList)
